import React, { useState, useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import AppLoader from '../shared/AppLoader';
import useAPI from '../utils/api';
import Button from '../shared/Button';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ConfirmationDialog from './Modals/ConfirmationDialog';

const normalizeWidgetType = (type) =>
  type?.replace('Widget::', '').replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

function Widgets() {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState('30'); // '0' (Today), '7', '30', '90'
  const API = useAPI();
  const { siteId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const getDateParams = (range) => {
    const to = new Date().toISOString().split('T')[0];
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - parseInt(range));
    const from = fromDate.toISOString().split('T')[0];
    return { from, to };
  };

  useEffect(() => {
    if (location.state?.success) {
      enqueueSnackbar(location.state.message || 'Success!', { variant: 'success' });
      // Clear state so it doesn't show again on refresh
      history.replace({ ...location, state: undefined });
    }
  }, [location, enqueueSnackbar, history]);

  useEffect(() => {
    const fetchWidgets = async () => {
      if (widgets.length === 0) setLoading(true);
      try {
        const [ok, data] = await API.chatWidgets(filter);
        if (ok && data && data.widgets) {
          const widgetList = data.widgets;
          setWidgets(widgetList);
          
          const { from, to } = getDateParams(dateRange);
          
          widgetList.forEach(async (w) => {
             const [statsOk, statsData] = await API.chatWidgetSummary(w.id, from, to);
             if (statsOk && statsData && statsData.numbers) {
               setStats(prev => ({ ...prev, [w.id]: statsData.numbers }));
             }
          });
        } else {
          setWidgets([]);
        }
      } catch (error) {
        console.error("Error fetching widgets", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWidgets();
  }, [siteId, filter, dateRange]);

  const filteredWidgets = widgets.filter(w => 
    w.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleWidget = async (widget) => {
    console.log("Toggling widget:", widget.id, "Current status:", widget.status);
    try {
      const [ok, data] = await API.toggleChatWidget(widget.id);
      console.log("API Response OK:", ok, "Data:", data);
      
      if (ok && data) {
        const updatedWidget = data.widget;
        
        if (updatedWidget) {
          try {
            window.analytics?.track(updatedWidget.status === 'active' ? 'Widget Activated' : 'Widget Paused', {
              widget_id: updatedWidget.id,
              widget_name: updatedWidget.name,
              widget_type: normalizeWidgetType(updatedWidget.type),
              site_id: siteId,
            });
          } catch (_) {}
          setWidgets(prevWidgets => {
            return prevWidgets.map(w => {
              if (Number(w.id) === Number(updatedWidget.id)) {
                return { ...w, ...updatedWidget };
              }
              return w;
            });
          });
        }
      } else {
        console.error("Toggle failed or data missing", data);
      }
    } catch (error) {
      console.error("Error toggling widget", error);
    }
  };

  const deleteWidget = async (widget) => {
    try {
      const [ok] = await API.deleteChatWidget(widget.id);
      if (ok) {
        try {
          window.analytics?.track('Widget Deleted', {
            widget_id: widget.id,
            widget_name: widget.name,
            widget_type: normalizeWidgetType(widget.type),
            site_id: siteId,
          });
        } catch (_) {}
        setWidgets(widgets.filter(w => w.id !== widget.id));
      }
    } catch (error) {
      console.error("Error deleting widget", error);
    }
  };

  if (loading) return <AppLoader visible={true} />;

  return (
    <div style={{ 
      backgroundColor: '#F2F1F1',
      minHeight: '100vh',
      padding: '0px'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '20px' 
      }}>
        <div>
          <h1 style={{ 
            color: '#23262A', 
            fontWeight: 'bold', 
            fontSize: '28px', 
            margin: 0,
            lineHeight: '1.2'
          }}>
            Your Widgets
          </h1>
          <p style={{
            color: '#777',
            fontSize: '15px',
            margin: '8px 0 0 0',
            maxWidth: '500px'
          }}>
            Manage your chat and AI summary widgets, and track how many visitors are reaching out to you.
          </p>
        </div>
        <Button
          variant="contained"
          onClick={() => history.push(`/sites/${siteId}/widgets/choose`)}
          label="+ Create New Widget"
          style={{ 
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: 'bold',
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
          }}
        />
      </div>

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto 20px auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#fff',
        padding: '10px 20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        border: '1px solid #E5E7EB'
      }}>
        <Tabs 
          value={filter} 
          onChange={(e, val) => setFilter(val)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': { fontSize: '14px', fontWeight: 'bold', minWidth: '80px' }
          }}
        >
          <Tab value="all" label="All" />
          <Tab value="active" label="Active" />
          <Tab value="paused" label="Paused" />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search widgets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '250px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CalendarTodayIcon style={{ color: '#6B7280', fontSize: '18px' }} />
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            size="small"
            sx={{ 
              width: '160px', 
              fontSize: '13px', 
              fontWeight: '600',
              '& .MuiSelect-select': { paddingY: '8.5px' }
            }}
          >
            <MenuItem value="0">Today</MenuItem>
            <MenuItem value="7">Last 7 Days</MenuItem>
            <MenuItem value="30">Last 30 Days</MenuItem>
            <MenuItem value="90">Last 90 Days</MenuItem>
          </Select>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
           <AppLoader visible={true} />
        </div>
      ) : filteredWidgets.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 40px', 
          background: '#fff', 
          borderRadius: '12px', 
          boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{ fontSize: '50px', marginBottom: '20px' }}>{search ? '🔍' : '💬'}</div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#23262A', fontWeight: 'bold' }}>
            {search ? 'No widgets match your search' : 'No widgets found'}
          </h3>
          <p style={{ margin: '0 0 25px 0', color: '#777', fontSize: '16px' }}>
            {search ? 'Try a different search term or check your filters.' : 'Drive more engagement by adding a widget to your website.'}
          </p>
          {!search && (
            <Button
              variant="contained"
              onClick={() => history.push(`/sites/${siteId}/widgets/choose`)}
              label="Get Started"
              style={{ padding: '10px 30px' }}
            />
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1200px', margin: '0 auto' }}>
          {filteredWidgets.map(widget => (
            <div key={widget.id} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '25px', 
              background: '#fff', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              borderRadius: '10px',
              border: '1px solid #efefef'
            }}>
                 <div>
                  <strong style={{ fontSize: '18px', color: '#23262A' }}>{widget.name || 'Unnamed Widget'}</strong>
                  <div style={{ marginTop: '5px', display: 'flex', gap: '15px', alignItems: 'center' }}>
                      <span style={{ 
                        fontSize: '11px', 
                        textTransform: 'uppercase', 
                        letterSpacing: '1px', 
                        fontWeight: 'bold',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        border: '1px solid',
                        ...(widget.type === 'Widget::AiSummary'
                          ? { background: '#F0FFF4', color: '#2F855A', borderColor: '#C6F6D5' }
                          : { background: '#F0F4F8', color: '#476684', borderColor: '#D1D9E0' })
                      }}>
                        {widget.type === 'Widget::AiSummary' ? 'AI Summary' : `${widget.settings?.channels?.length || 0} ${widget.settings?.channels?.length === 1 ? 'Channel' : 'Channels'}`}
                      </span>
                     <span style={{ fontSize: '13px', color: '#888' }}>
                       Placement: <strong>{widget.placement || 'bottom-right'}</strong>
                     </span>
                     <span style={{ fontSize: '13px', color: '#888' }}>
                       Targeting: <strong>{widget.rule_name || 'Everyone'}</strong>
                     </span>
                  </div>
                </div>

              <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center', minWidth: '70px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#23262A' }}>
                    {stats[widget.id]?.total_humanized || '0'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Views</div>
                </div>

                <div style={{ textAlign: 'center', minWidth: '70px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#23262A' }}>
                    {stats[widget.id]?.conversion_humanized || '0'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Clicks</div>
                </div>

                <div style={{ textAlign: 'center', minWidth: '70px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ED5A29' }}>
                    {stats[widget.id] ? 
                      ((parseInt(stats[widget.id].conversion) / Math.max(parseInt(stats[widget.id].total), 1)) * 100).toFixed(1) : 
                      '0.0'}%
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>CTR</div>
                </div>

                <div style={{ width: '1px', height: '30px', background: '#EEE', margin: '0 5px' }}></div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Tooltip title={widget.status === 'active' ? 'Pause Widget' : 'Activate Widget'}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                       <Switch 
                         checked={widget.status === 'active'} 
                         onChange={() => toggleWidget(widget)}
                         color="primary"
                         size="small"
                       />
                    </div>
                  </Tooltip>

                  <div style={{ width: '10px' }}></div>

                  <Tooltip title="Edit Widget">
                    <IconButton 
                      onClick={() => history.push(`/sites/${siteId}/widgets/${widget.id}/edit`)}
                      size="small"
                      style={{ color: '#ED5A29', background: '#FFF0EB' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <ConfirmationDialog 
                    onConfirm={() => deleteWidget(widget)}
                    component={(props) => (
                      <Tooltip title="Delete Widget">
                        <IconButton 
                          {...props}
                          size="small"
                          style={{ color: '#d32f2f', background: '#FFEBEE' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  >
                    Are you sure you want to delete this widget? This action cannot be undone.
                  </ConfirmationDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Widgets;
